<template>
    <v-container class="salesforce-search">
        <v-row>
            <v-col cols="12">
                <v-form  v-model="valid" v-on:submit.prevent>
                    <v-text-field label="Suchbegriff" hint="z.B. GP-Nummer, Email, Name, SSO-ID" :rules="rules" :disabled="loading" :clearable="true" clear-icon="mdi-close-circle" v-model="query" @keydown.enter="triggerSearch">
                        <template v-slot:append-outer>
                            <v-btn outlined @click="triggerSearch" :disabled="loading || !valid" :loading="loading">
                                <v-icon left>mdi-account-search</v-icon> Suchen
                            </v-btn>
                        </template>
                    </v-text-field>
                </v-form>
            </v-col>
        </v-row>
        <v-row v-if="resultFor.length">
            <v-col cols="12">
                <h1 class="title">{{ resultFor }}</h1>
            </v-col>
        </v-row>
        <v-row v-if="errorMessage.length > 0">
            <v-col cols="12">
                <v-alert type="error">
                    {{ errorMessage }}
                </v-alert>
            </v-col>
        </v-row>
        <v-row v-if="resultFor.length">
            <v-col cols="12">
                <v-data-table
                    :headers="headers"
                    hide-default-footer
                    disable-pagination
                    :items="result"
                    :loading="loading"
                    :items-per-page="50"
                    @click:row="doubleClick"
                >
                    <template slot="item" slot-scope="props">
                        <tr :class="{'salesforce-search__werbesperre': props.item.hatWerbesperre}">
                            <td class="d-flex align-center">
                                <span>{{ props.item.id }}</span>
                                <v-tooltip right v-if="props.item.hatWerbesperre">
                                    <template #activator="{ on }">
                                        <v-icon v-on="on" class="ml-2 salesforce-search__werbesperre-icon">mdi-information</v-icon>
                                    </template>
                                    <span>Achtung: Werbesperre</span>
                                </v-tooltip>
                            </td>
                            <td>{{ props.item.name }}</td>
                            <td>{{ props.item.email }}</td>
                            <td>{{ props.item.birthdate }}</td>
                            <td>{{ props.item.gpNumber }}</td>
                            <td>{{ props.item.ssoId }}</td>
                            <td>{{ props.item.sfId }}</td>
                            <td>
                                <div class="d-flex">
                                    <div class="flex-none" v-if="props.item.existInC1 || props.item.existInSap">
                                        <v-tooltip left>
                                            <template #activator="{ on }">
                                                <v-btn
                                                        v-on="on"
                                                        text
                                                        icon
                                                        class="drei_60-black-text"
                                                        @click="showDetails(props.item)"
                                                >
                                                    <v-icon>mdi-account-circle</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Nutzerdetails</span>
                                        </v-tooltip>
                                    </div>
                                    <div class="flex-none">
                                        <v-tooltip left>
                                            <template #activator="{ on }">
                                                <v-btn
                                                    v-on="on"
                                                    text
                                                    icon
                                                    class="drei_60-black-text"
                                                    :href="`https://nozmedien.my.salesforce.com/${props.item.sfId}`"
                                                    target="_blank"
                                                >
                                                    <v-icon>mdi-account-box-multiple</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Salesforce-Account</span>
                                        </v-tooltip>
                                    </div>
                                    <div class="flex-none">
                                        <v-tooltip left>
                                            <template #activator="{ on }">
                                                <v-btn
                                                    v-on="on"
                                                    text
                                                    icon
                                                    class="drei_60-black-text"
                                                    :href="`https://nozmedien--c.eu19.visual.force.com/apex/PermB2c_VF?accountID=${props.item.sfId}`"
                                                    target="_blank"
                                                >
                                                    <v-icon>mdi-bullhorn</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Werbesperren</span>
                                        </v-tooltip>
                                    </div>
                                    <EditEmail
                                            v-if="props.item.existInC1"
                                            alternateButton="false"
                                            :ssoId="props.item.ssoId"
                                    />
                                    <ResetPassword :ssoId="props.item.ssoId" v-if="props.item.existInC1" />
                                </div>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import Route from '@/route';
import Util from '@/util';
import EditEmail from '@/components/customer/EditEmail.vue';
import ResetPassword from '@/components/customer/ResetPassword.vue';

export default {
    name: 'SalesforceSearch',
    components: {
        EditEmail,
        ResetPassword,
    },
    data() {
        return {
            valid: false,
            query: '',
            clicks: 0,
            delay: 700,
            timer: null,
            lastClickedIndex: -1,
            headers: [{
                text: '#',
                align: 'left',
                sortable: false,
                value: 'id',
            }, {
                text: 'Name',
                align: 'left',
                value: 'name',
                sortable: false,
            }, {
                text: 'Email',
                align: 'left',
                value: 'email',
                sortable: false,
            }, {
                text: 'Geburtsdatum',
                value: 'birthdate',
                align: 'left',
                sortable: false,
            }, {
                text: 'GP-Nummer',
                align: 'left',
                value: 'gpNumber',
                sortable: false,
            }, {
                text: 'SSO-ID',
                align: 'left',
                value: 'ssoId',
                sortable: false,
            }, {
                text: 'Salesforce-ID',
                align: 'left',
                value: 'sfId',
                sortable: false,
            }, {
                text: 'Aktionen',
                value: 'action',
                sortable: false,
            }],
        };
    },
    computed: {
        ...mapState('salesforceSearch', [
            'loading',
            'rules',
            'errorMessage',
        ]),
        ...mapGetters('salesforceSearch', ['result', 'resultFor']),
    },
    methods: {
        triggerSearch() {
            if (this.valid) {
                this.search({ query: this.query });
            }
        },
        showDetails(customer) {
            if (customer.existInC1) {
                this.$router.push({
                    name: Route.CUSTOMER_C1.name,
                    params: {
                        sso_id: customer.ssoId,
                    },
                });
            } else if (customer.existInSap) {
                this.$router.push({
                    name: Route.CUSTOMER_MSP.name,
                    params: {
                        gp_id: customer.gpNumber,
                        scope: Util.scopeFromGp(customer.gpNumber),
                    },
                });
            }
        },
        doubleClick(item) {
            this.clicks += 1;
            if (this.clicks === 1) {
                const self = this;
                this.timer = setTimeout(() => {
                    self.clicks = 0;
                }, this.delay);
            } else if (this.lastClickedIndex === item.id) {
                this.showDetails(item);
                clearTimeout(this.timer);
                this.clicks = 0;
            } else {
                const self = this;
                clearTimeout(this.timer);
                this.timer = setTimeout(() => {
                    self.clicks = 0;
                }, this.delay);
                this.clicks = 1;
            }
            this.lastClickedIndex = item.id;
        },
        ...mapActions('salesforceSearch', ['search']),
    },
};
</script>

<style scoped lang="scss">
    @import "@/style/colors.scss";
    .salesforce-search__werbesperre,
    .salesforce-search__werbesperre-icon {
        color: $red;
    }
</style>
